import React, { useState, useEffect } from 'react';
import ElevenLabsAgents from './ElevenLabsAgents';
import './Admin.css';

function Admin({ user }) {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('users');

  // Check for tab parameter in URL when component mounts
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
  }, []);

  // Fetch all users
  useEffect(() => {
    if (activeTab !== 'users') return;
    
    const fetchUsers = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/admin/users', {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        
        const data = await response.json();
        setUsers(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUsers();
  }, [activeTab]);

  // Handle role change
  const updateUserRole = async (userId, newRole) => {
    try {
      setSuccessMessage('');
      
      // Validate the role
      if (!['admin', 'regular', 'waiting', 'archived'].includes(newRole)) {
        setError('Invalid role selected');
        return;
      }
      
      const response = await fetch(`/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ role: newRole }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update user role');
      }
      
      const data = await response.json();
      
      // Update users list
      setUsers(users.map(user => 
        user.id === userId ? { ...user, role: newRole } : user
      ));
      
      setSuccessMessage(data.message || `User role updated to ${newRole} successfully`);
    } catch (err) {
      setError(err.message);
    }
  };

  // If not admin, redirect
  if (user.role !== 'admin') {
    return (
      <div className="admin-container">
        <h2>Access Denied</h2>
        <p>You do not have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="admin-wrapper">
      <div className="admin-header">
        <h2>Admin Dashboard</h2>
        <div className="admin-tabs">
          <button 
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            User Management
          </button>
          <button 
            className={`tab-button ${activeTab === 'meetings' ? 'active' : ''}`}
            onClick={() => setActiveTab('meetings')}
          >
            Meeting Management
          </button>
          <button 
            className={`tab-button ${activeTab === 'elevenlabs' ? 'active' : ''}`}
            onClick={() => setActiveTab('elevenlabs')}
          >
            ElevenLabs Agents
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}
      
      <div className="admin-container">
        {activeTab === 'users' && (
          <>
            <h3>User Management</h3>
            {isLoading ? (
              <p>Loading users...</p>
            ) : (
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} className={user.role === 'archived' ? 'archived-row' : ''}>
                        <td>{user.id}</td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`role-indicator ${user.role}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                        <td>
                          <select 
                            value={user.role}
                            onChange={(e) => updateUserRole(user.id, e.target.value)}
                            className="role-select"
                          >
                            <option value="admin">Admin</option>
                            <option value="regular">Regular</option>
                            <option value="waiting">Waiting</option>
                            <option value="archived">Archived</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'meetings' && (
          <div className="meetings-section">
            <h3>Meeting Management</h3>
            <p>
              <a 
                href="https://ai-twilio-3ac8.myjava.ai/conversation-admin.html"
                target="_blank"
                rel="noopener noreferrer"
                className="meeting-link"
              >
                Click here to manage meetings
              </a>
            </p>
            <p>
              <a 
                href="https://ai-twilio-3ac8.myjava.ai/transcripts"
                target="_blank"
                rel="noopener noreferrer"
                className="meeting-link"
              >
                Click here to See Transcripts
              </a>
            </p>
            <p className="note">Note: These will open in new tabs.</p>
          </div>
        )}
        
        {activeTab === 'elevenlabs' && (
          <ElevenLabsAgents />
        )}
      </div>
    </div>
  );
}

export default Admin;